import { Message, Subscriber } from 'components/sales/types';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { buildMessageDetailsNavigation } from './navigation';
import {
  MessageInputsPlaceholderSection,
  MessageInputsSection,
  OverviewSection,
  SubscriberDetailsSection,
} from './sections';
import { MessageDetails } from './types';

const BodyStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
`;

interface MessageDetailsBodyProps {
  // Null when the details API has nothing for this message. The layout still
  // renders everything derivable from live props (message bubble, feedback,
  // subscriber identity); only Message Inputs collapses to a placeholder.
  payload: MessageDetails | null;
  // Live sent message + subscriber for the Overview feedback affordance.
  message?: Message;
  subscriber?: Subscriber | null;
  // Placeholder treatment when payload is null: 'empty' (snapshot doesn't
  // exist) vs 'error' (couldn't load it — pairs with onRetry).
  detailsUnavailable?: 'empty' | 'error';
  onRetry?: () => void;
  // Admin-only contract editor entry for the 'empty' placeholder.
  onOpenEditor?: () => void;
}

// Rows the API payload drives when a section renders without one.
const NO_PAYLOAD_SUBSCRIBER_ROWS: Pick<
  MessageDetails,
  'customer_states' | 'lifecycle_stages'
> = {
  customer_states: [],
  lifecycle_stages: [],
};

// Two-section redesign: message preview (OverviewSection) + Message Inputs +
// Subscriber Details. Spec 2026-06-08 §3. Input rows navigate (new tab) to the
// Brand Center areas that configure them. Spec 2026-06-11 §3.
export const MessageDetailsBody: React.FC<MessageDetailsBodyProps> = ({
  payload,
  message,
  subscriber = null,
  detailsUnavailable = 'empty',
  onRetry,
  onOpenEditor,
}) => {
  const navigation = useMemo(
    () => (payload ? buildMessageDetailsNavigation(payload) : undefined),
    [payload],
  );

  return (
    <BodyStack>
      <OverviewSection
        details={payload ?? { full_reference_text: null }}
        message={message}
        subscriber={subscriber}
      />
      {payload ? (
        <MessageInputsSection details={payload} onNavigate={navigation} />
      ) : (
        <MessageInputsPlaceholderSection
          variant={detailsUnavailable}
          onRetry={onRetry}
          onOpenEditor={onOpenEditor}
        />
      )}
      <SubscriberDetailsSection
        details={payload ?? NO_PAYLOAD_SUBSCRIBER_ROWS}
        subscriber={subscriber}
      />
    </BodyStack>
  );
};
